import { Fragment, useEffect, useState } from 'react'
import {
  Alert, Box, Chip, CircularProgress, Link, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { reachApi, ReachOverview } from '../services/api'
import { attackPathsApi, GuardrailCoverage as Coverage } from '../services/api'
import GuardrailCoverage from './GuardrailCoverage'
import { useAttackPaths } from '../hooks/useAttackPaths'
import { ROUTE_MEANING, VERDICT, worstVerdict } from './exposureVerdicts'
import { InjectionVerdict } from '../services/api'
import BundleTeaser from './BundleTeaser'
import { BUNDLE_COPY } from './bundleCopy'
import useCanContactVendor from '../hooks/useCanContactVendor'

/**
 * The knowledge layer, as an inventory.
 *
 * This lives inside the Registry rather than on a page of its own because the
 * Registry is already the "what do I have" surface, and no customer's first
 * question is "show me my components" — they arrive asking about an agent and
 * follow the thread. The questions that *are* destinations are answered
 * elsewhere: what an agent touches is on its card, who depends on a store is
 * the Blast Radius lens, and a disagreement between two repositories is a
 * finding.
 *
 * What this view adds is the part only an inventory can say — the estate at a
 * glance, and specifically the components nothing in it declares.
 */

const KIND_LABELS: Record<string, string> = {
  vector_store: 'vector store',
  search_engine: 'search engine',
  graph_store: 'graph store',
  managed_kb: 'knowledge base',
  object_store: 'object store',
  hosted_store: 'hosted store',
  embedding_model: 'embedding model',
  mcp_server: 'MCP server',
  a2a_endpoint: 'A2A endpoint',
  retrieval_endpoint: 'retrieval endpoint',
  inference_endpoint: 'inference endpoint',
  model_artifact: 'model artifact',
}

/**
 * How a component's retrievals narrow their results, written for whoever reads
 * the table rather than for whoever wrote the detector.
 *
 * Two rules these had broken. **Say what was observed, not what it implies.**
 * The `mixed` copy asserted that an unscoped query returns "another tenant's
 * documents", which we do not know: the filter we saw might be by customer,
 * legal entity, region or classification, and the collection may hold one
 * population, in which case the sentence is simply false. **And leave the
 * mechanism out.** "In a query DSL the search and the tenant filter are the
 * same construct" is true and is a note to ourselves.
 *
 * `mixed` remains the only one coloured, because it is the only one that is a
 * finding: the customer's own code treating one collection two ways. A
 * single-tenant corpus retrieved without a filter is ordinary, and colouring
 * it would teach a reader to ignore the colour.
 */
const SCOPING: Record<string, { label: string; color: 'warning' | 'default'; title: string }> = {
  mixed: {
    label: 'inconsistent',
    color: 'warning',
    title: 'Some retrievals from this component narrow what they return and others return everything that matches. One collection is being treated two ways, so one of them is wrong — and which one depends on whether this holds data for more than one customer, entity or region.',
  },
  scoped: {
    label: 'scoped',
    color: 'default',
    title: 'Every retrieval we can see narrows what it returns before answering — by namespace, partition or filter.',
  },
  unknown: {
    label: 'not read',
    color: 'default',
    title: 'These retrievals are written in a form we could not read, so we cannot say whether they narrow their results. Not evidence either way.',
  },
  unscoped: {
    label: 'unscoped',
    color: 'default',
    title: 'No retrieval we can see narrows what it returns. That is normal where the whole collection is meant to be readable by every caller, and worth checking where it is not.',
  },
}

const isDigest = (id: string) => id.startsWith('ep:')
const shortId = (id: string) => (isDigest(id) ? `${id.slice(0, 11)}…` : id)

/**
 * Protocol surfaces are not the knowledge layer. An MCP server or an A2A
 * endpoint already has its own node type on the mesh, its own chips on the
 * agent card, and — when this repository hosts one — its own row in the
 * Agents view, carrying the tools it exposes. Listing it here as well put the
 * same name in front of a reader three times with three different shapes, and
 * a tab called "Knowledge layer" is the wrong one of the three.
 */
const PROTOCOL_KINDS = new Set(['mcp_server', 'a2a_endpoint'])

interface Props {
  /** The page's own agent rows, keyed by hint — see onSelectAgent. */
  agents: any[]
  /** Opens the Registry's existing agent detail dialog. Deliberately reusing
   *  it rather than linking away: the reader asked who depends on this store
   *  while looking at an inventory, and the answer belongs on the same page. */
  onSelectAgent: (agent: any) => void
}

const ComponentsView = ({ agents, onSelectAgent }: Props) => {
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState<ReachOverview | null>(null)
  const [notEntitled, setNotEntitled] = useState(false)
  // ARIAS Exposure. Estate-level, like the component inventory itself, which is
  // why it lives here rather than in the per-analysis coverage drawer.
  // `null` means the route refused; the teaser goes where the card would.
  const [coverageExposure, setCoverageExposure] = useState<Coverage | null>(null)
  const [exposureRefused, setExposureRefused] = useState(false)
  // The component side of each route. Same rows the agent card renders,
  // indexed the other way: an agent asks "what reaches me", a component asks
  // "who reads what I hold". One fetch, two indexes.
  const { byOrigin: routesByOrigin } = useAttackPaths()
  const [expanded, setExpanded] = useState<string | null>(null)
  const canContactVendor = useCanContactVendor()

  useEffect(() => {
    attackPathsApi
      .guardrailCoverage()
      .then((r) => setCoverageExposure(r.data))
      // A 404 is the ordinary case for a tenant without Exposure, not an error
      // worth showing anyone.
      .catch(() => setExposureRefused(true))
    reachApi.overview()
      .then(r => setOverview(r.data))
      .catch(e => { if (e?.response?.status === 404) setNotEntitled(true) })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>

  if (notEntitled) {
    return (
      <Alert severity="info">
        Agent Reach is not enabled for your tenant.{' '}
        {canContactVendor
          ? 'Contact sales@tryarias.com to see the stores, indexes and corpora your agents depend on.'
          : 'Ask your tenant admin to enable it — it shows the stores, indexes and corpora your agents depend on.'}
      </Alert>
    )
  }

  const byHint = new Map<string, any>(
    (agents || []).map(a => [a.agent_id_hint, a]),
  )
  const components = (overview?.components || []).filter(
    c => !PROTOCOL_KINDS.has(c.component_kind),
  )
  const unmatched = overview?.reached_but_not_declared || []
  const unresolved = overview?.unresolved_components || []
  const coverage = overview?.coverage

  if (!components.length && !unmatched.length) {
    return (
      <Alert severity="info">
        <strong>No knowledge-layer components yet.</strong> They appear once a scanned
        repository declares a store, index or corpus — including one that holds only
        configuration and no agent code.
      </Alert>
    )
  }

  return (
    <Box>
      {/* The estate in a sentence, before any table. A reader should know
          where they stand without counting rows. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>{components.length}</strong> component
        {components.length === 1 ? '' : 's'} across{' '}
        <strong>{new Set(components.flatMap(c => c.declared_in)).size}</strong> repositor
        {new Set(components.flatMap(c => c.declared_in)).size === 1 ? 'y' : 'ies'}
        {coverage ? <>, from <strong>{coverage.scans_considered}</strong> scan
          {coverage.scans_considered === 1 ? '' : 's'}</> : null}
        {components.some(c => c.cross_repo) && (
          <>. <strong>{components.filter(c => c.cross_repo).length}</strong> declared in one
          repository and reached from another — the relationship a per-repository scan
          cannot see</>
        )}.
      </Typography>

      {/* The most actionable thing this view can say, and the reason it leads.
          It names exactly what to scan next. */}
      {unmatched.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{unmatched.length}</strong> component
          {unmatched.length === 1 ? ' is' : 's are'} referenced by your agents and
          declared in no repository you have scanned. Usually the repository that owns
          them has not been scanned yet — scanning it fills in what they are and what
          they hold.
          <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.78rem', mt: 0.5 }}>
            {unmatched.slice(0, 8).map(shortId).join(', ')}
            {unmatched.length > 8 ? ` and ${unmatched.length - 8} more` : ''}
          </Box>
        </Alert>
      )}

      {/* What nobody declared. Every claim this product makes about who may
          reach sensitive data rests on a tag the customer wrote, so an estate
          that is mostly untagged is not a safe estate — it is an unassessed
          one, and the two look identical everywhere else. Shown as a limit on
          the evidence rather than as an accusation, because the remedy is a
          task the customer can actually do. */}
      {(coverage?.components_unclassified ?? 0) > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{coverage!.components_classified}</strong> of{' '}
          <strong>{coverage!.components_holding_data}</strong> components that
          hold data carry a declared sensitivity. Controls about data handling are
          assessed on those; the rest are not assessed, and an unassessed component
          looks the same as a safe one in every report.
          {coverage!.components_classification_inherited > 0 && (
            <> {coverage!.components_classification_inherited} of the tagged ones
            borrowed the tag from something that fills them rather than carrying one
            directly.</>
          )}
        </Alert>
      )}

      {unresolved.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{unresolved.length}</strong> component
          {unresolved.length === 1 ? '' : 's'} exist but their names are built at
          runtime. They are real; we simply cannot match them to anything else by name,
          and guessing would create a link that is not there.
        </Alert>
      )}

      {coverageExposure && <GuardrailCoverage coverage={coverageExposure} />}
      {exposureRefused && (
        <Box sx={{ mb: 2 }}>
          <BundleTeaser
            copy={BUNDLE_COPY.attack_paths}
            canContactVendor={canContactVendor}
          />
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell>Declared in</TableCell>
              <TableCell align="right">Agents reaching it</TableCell>
              {/* Exposure's depth on the object this table already lists. The
                  column appears only when the tenant has it, rather than
                  rendering an empty column for everyone. */}
              {/* The header carried no explanation of either word, and the
                  chips' own tooltips described the verdict without saying what
                  it was a verdict ABOUT — so a reader could learn something
                  was "reachable" without learning what had reached what. */}
              {routesByOrigin && (
                <TableCell>
                  <Tooltip title={ROUTE_MEANING}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                      Route verdict
                      <HelpOutline sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Box>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell>Retrieval scope</TableCell>
              <TableCell>Classification</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...components]
              .sort((a, b) => b.dependent_count - a.dependent_count)
              .map(c => (
              <Fragment key={c.identity}>
                <TableRow hover>
                  <TableCell>
                    <Tooltip title={isDigest(c.identity)
                      ? 'A network address, stored as a one-way digest rather than transmitted in full.'
                      : c.identity}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {shortId(c.identity)}
                      </Typography>
                    </Tooltip>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {KIND_LABELS[c.component_kind] || c.component_kind.replace(/_/g, ' ')}
                      </Typography>
                      {c.recognition === 'structural' && (
                        <Tooltip title="Recognised by what the code does with it — a query vector and a neighbour count, a dimension and a distance metric — rather than by a product we know by name. Worth confirming before acting on it.">
                          <Chip size="small" variant="outlined" label="inferred"
                            sx={{ height: 16, fontSize: '0.6rem' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {c.declared_in.join(', ') || '—'}
                    </Typography>
                    {c.cross_repo && (
                      <Tooltip title="Declared in one repository and reached from another.">
                        <Chip size="small" label="cross-repo" color="primary" variant="outlined"
                          sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {c.dependent_count ? (
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => setExpanded(expanded === c.identity ? null : c.identity)}
                      >
                        {c.dependent_count}
                      </Link>
                    ) : '—'}
                  </TableCell>
                  {routesByOrigin && (
                    <TableCell>
                      {(() => {
                        const routes = routesByOrigin[c.identity] ?? []
                        // An em dash, not "no routes". A component with no
                        // assessed route may genuinely route nowhere, or may
                        // have been assessed under a different identity — and
                        // either way this table is not the place to assert it.
                        if (!routes.length) {
                          return (
                            <Tooltip title="No assessed route starts here. That is not a statement that none exists — a route is only assessed where both the component and an agent reading it were scanned.">
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            </Tooltip>
                          )
                        }
                        const worst = worstVerdict(
                          routes.map(r => r.verdict) as InjectionVerdict[],
                        )
                        if (!worst) return null
                        const same = routes.filter(r => r.verdict === worst).length
                        return (
                          <Tooltip title={VERDICT[worst].meaning}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                size="small"
                                variant="outlined"
                                color={VERDICT[worst].color}
                                label={VERDICT[worst].label}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {/* The worst verdict, with how many routes share
                                  it — a component feeding one reachable agent
                                  and one feeding nine are different problems
                                  and the chip alone cannot tell them apart. */}
                              <Typography variant="caption" color="text.secondary">
                                {same} of {routes.length}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        )
                      })()}
                    </TableCell>
                  )}
                  <TableCell>
                    {(() => {
                      const verdict = c.scoping?.verdict
                      const shown = verdict ? SCOPING[verdict] : null
                      if (!shown) {
                        return (
                          <Tooltip title="Nothing we scanned retrieves from this, so we have no reading on how its queries are scoped. Not the same as unscoped.">
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          </Tooltip>
                        )
                      }
                      const fixed = verdict === 'scoped' && c.scoping?.scoped_by === 'constant'
                      return (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title={shown.title}>
                            <Chip size="small" variant="outlined" label={shown.label}
                              color={shown.color} sx={{ height: 18, fontSize: '0.6rem' }} />
                          </Tooltip>
                          {fixed && (
                            <Tooltip title="The scope is a value written into the source rather than one derived from the request. It passes any check for whether a filter is present and stops separating anything the moment a second tenant shares the collection.">
                              <Chip size="small" variant="outlined" label="fixed in source"
                                sx={{ height: 18, fontSize: '0.6rem' }} />
                            </Tooltip>
                          )}
                        </Stack>
                      )
                    })()}
                  </TableCell>
                  <TableCell>
                    {c.classification ? (
                      <Tooltip title={c.classification_inherited_from
                        ? `Inherited from ${c.classification_inherited_from} — not declared on this component itself.`
                        : 'Declared on this component.'}>
                        <Chip size="small" variant="outlined" label={c.classification} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No classification tag. Unlabelled is not the same as not sensitive.">
                        <Typography variant="caption" color="text.secondary">untagged</Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                {expanded === c.identity && (
                  <TableRow>
                    {/* Tracks the header: the Route verdict column only exists when the
                        tenant has Exposure, so a fixed 5 would leave the expanded
                        row one short and the detail panel visibly narrower than
                        the table above it. */}
                    <TableCell colSpan={routesByOrigin ? 6 : 5} sx={{ bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Agents retrieving from this
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {c.reached_by.map(d => {
                          const agent = byHint.get(d.agent_id_hint)
                          return agent ? (
                            <Chip
                              key={d.agent_id_hint}
                              size="small"
                              variant="outlined"
                              clickable
                              label={agent.display_name || agent.code_identifier || d.agent_id_hint}
                              onClick={() => onSelectAgent(agent)}
                            />
                          ) : (
                            // Reached by an agent this tenant's registry does
                            // not hold — usually a repository scanned once and
                            // since removed. Named, not hidden, and not
                            // clickable, because there is nothing to open.
                            <Tooltip key={d.agent_id_hint} title="This agent is not in the current registry.">
                              <Chip size="small" variant="outlined" disabled
                                label={d.agent_id_hint.slice(0, 10)} />
                            </Tooltip>
                          )
                        })}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ComponentsView
